import axiosInstance from '../api/axiosInstance';
import { EcommerceAPIResponse, EcommerceOrder, EcommerceStore, EcommerceStatus } from '../types/ecommerce';
import ofimaSyncService from '../api/ofimaSyncService'

export class EcommerceService {
  private static baseURL = '/ecommerce';

  /**
   * Obtener pedidos de un ecommerce específico
   */
  static async getOrders(params: {
    store: string;
    page?: number;
    per_page?: number;
    status?: string;
    search?: string;
    date_created_after?: string;
    date_created_before?: string;
  }): Promise<EcommerceAPIResponse> {
    try {
      const token = localStorage.getItem('accessToken');
      const queryParams = new URLSearchParams();
      
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          queryParams.append(key, value.toString());
        }
      });

      const response = await axiosInstance.get(
        `${this.baseURL}/orders?${queryParams.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000 // 30 segundos timeout
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error fetching ecommerce orders:', error);
      throw new Error(`Error al obtener pedidos del ecommerce: ${error}`);
    }
  }

  /**
   * Obtener pedido específico por ID
   */
  static async getOrderById(id: string, store: string): Promise<EcommerceOrder> {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axiosInstance.get(
        `${this.baseURL}/orders/${id}?store=${store}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000
        }
      );

      return response.data.data;
    } catch (error) {
      console.error('Error fetching ecommerce order:', error);
      throw new Error(`Error al obtener pedido del ecommerce: ${error}`);
    }
  }

  /**
   * Obtener múltiples remisiones de OFIMA por IDs separados por comas
   */
  static async getOFIMARemisiones(ids: string[]): Promise<{
    success: boolean;
    message: string;
    data: EcommerceOrder[] | null;
    numeros: string[];
    total: number;
  }> {
    try {
      const token = localStorage.getItem('accessToken');
      const idsString = ids.join(',');
      
      const response = await axiosInstance.get(
        `${this.baseURL}/orders/${idsString}?store=OFIMA`,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 60000 // 60 segundos para múltiples IDs
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error fetching OFIMA remisiones:', error);
      throw new Error(`Error al obtener remisiones de OFIMA: ${error}`);
    }
  }

  /**
   * Dado un listado de IDs (SGL), retorna el conjunto de IDs que NO tienen id_remision en OFIMA
   */
  static async getOrdersWithoutRemision(ids: Array<string | number>): Promise<Set<string>> {
    if (!ids || ids.length === 0) return new Set()
    const idsAsStrings = ids.map(id => id.toString())

    // Usar endpoint POST /ofima-sync/ejecutar-y-consultar (baseURL ya incluye /api)
    const result = await ofimaSyncService.ejecutarYConsultar(idsAsStrings)
    const pedidos = Array.isArray(result?.pedidos) ? result.pedidos : []

    // Consideramos como "sin remisión" aquellos que no traen NumeroOfima aunque se haya ejecutado el proceso
    const missing = new Set<string>()
    for (const numero of idsAsStrings) {
      const detalle = pedidos.find(p => p.Numero === numero)
      if (!detalle || !detalle.NumeroOfima || detalle.NumeroOfima.toString().trim() === '') {
        missing.add(numero)
      }
    }
    return missing
  }

  /**
   * Obtener tiendas disponibles
   */
  static async getStores(): Promise<EcommerceStore[]> {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axiosInstance.get(
        `${this.baseURL}/stores`,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000
        }
      );

      return response.data.data;
    } catch (error) {
      console.error('Error fetching ecommerce stores:', error);
      throw new Error(`Error al obtener tiendas: ${error}`);
    }
  }

  /**
   * Obtener estados de una tienda específica
   */
  static async getStoreStatuses(store: string): Promise<EcommerceStatus[]> {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axiosInstance.get(
        `${this.baseURL}/stores/${encodeURIComponent(store)}/statuses`,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000
        }
      );

      return response.data.data;
    } catch (error) {
      console.error('Error fetching store statuses:', error);
      throw new Error(`Error al obtener estados de la tienda: ${error}`);
    }
  }

  /**
   * Verificar conectividad con el ecommerce
   */
  static async checkConnection(): Promise<boolean> {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axiosInstance.get(
        `${this.baseURL}/connection`,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000
        }
      );

      return response.data.success;
    } catch (error) {
      console.error('Error checking ecommerce connection:', error);
      return false;
    }
  }

  /**
   * Obtener información de una tienda específica
   */
  static async getStoreInfo(store: string): Promise<any> {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axiosInstance.get(
        `${this.baseURL}/stores/${encodeURIComponent(store)}/info`,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000
        }
      );

      return response.data.data;
    } catch (error) {
      console.error('Error fetching store info:', error);
      throw new Error(`Error al obtener información de la tienda: ${error}`);
    }
  }
}
